import React from 'react';
import { ArrowRightIcon } from '@/components/ui/Icons';
import { useRedirect } from '@/context/RedirectContext';
import { REDIRECT_DELAY_MS } from '@/config';

const RedirectOverlay: React.FC = () => {
    const { isRedirecting, redirectSite, redirectUrl, isPopupBlocked, cancelRedirect } = useRedirect();

    if (!isRedirecting) return null;

    const finalRedirectUrl = redirectUrl;

    const durationSeconds = REDIRECT_DELAY_MS / 1000;

    // Circle progress calculation (r=36 inside a 96x96 viewBox)
    // circumference = 2 * PI * r = 2 * 3.14159 * 36 = 226.195
    const radius = 36;
    const strokeWidth = 4.5;
    const circumference = 2 * Math.PI * radius;

    return (
        <div 
            className="redirect-overlay-container fixed inset-0 z-[1000] flex items-center justify-center p-4 overflow-hidden"
            style={{
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                '--circumference': `${circumference}px`,
                '--duration': `${durationSeconds}s`,
            } as React.CSSProperties}
        >
            {/* Backdrop click to cancel */}
            <div className="absolute inset-0 bg-black/0 cursor-pointer z-0" onClick={cancelRedirect} />

            {/* Premium double-bordered squircle card wrapper */}
            <div 
                className="animate-scale-up card-premium-glow card-border-wrapper relative z-10 w-full max-w-[340px] rounded-[2.75rem] p-[1.5px] transition-all duration-500 ease-out hover:scale-[1.025]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Inner glass card container */}
                <div 
                    className="redirect-card-body relative overflow-hidden rounded-[calc(2.75rem-1.5px)] px-8 py-9 sm:px-9 sm:py-10 flex flex-col items-center text-center transition-colors duration-300"
                >
                    {/* Ambient deep red radial background glow */}
                    {!isPopupBlocked && (
                        <div 
                            className="absolute top-[35px] w-36 h-36 rounded-full bg-red-600/10 blur-[35px] pointer-events-none transition-opacity duration-300 animate-pulse"
                        />
                    )}

                    {isPopupBlocked ? (
                        /* ═══ Blocked State ═══ */
                        <div className="w-full flex flex-col items-center z-10">
                            {/* Glowing Warning Ring Indicator */}
                            <div className="w-20 h-20 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-6 relative">
                                <span className="absolute inset-0 rounded-full bg-red-500/15 animate-ping" />
                                <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.25}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            
                            <h4 className="text-lg font-black mb-1.5 text-[var(--main-text)] tracking-tight">
                                Pop-up Blocked
                            </h4>
                            <p className="text-xs text-[var(--secondary-text)] mb-6 max-w-[230px] leading-relaxed">
                                Your browser blocked the automatic redirect. Click below to continue.
                            </p>
                            
                            <a 
                                href={finalRedirectUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                onClick={cancelRedirect} 
                                className="cta-button pulse w-full py-3.5 px-6 rounded-[1.25rem] text-white font-extrabold text-sm inline-flex items-center justify-center cursor-pointer transition-all hover:brightness-110 active:scale-[0.98] min-w-0"
                            >
                                <span className="flex items-center justify-center gap-2 max-w-full min-w-0">
                                    <span className="truncate whitespace-nowrap">Open {redirectSite}</span>
                                    <ArrowRightIcon className="w-4 h-4 shrink-0" />
                                </span>
                            </a>
                        </div>
                    ) : (
                        /* ═══ Standard Redirect State ═══ */
                        <div className="w-full flex flex-col items-center z-10">
                            {/* Cinematic Centered SVG Circle Loader */}
                            <div className="relative w-24 h-24 flex items-center justify-center mb-7">
                                <svg className="absolute inset-0 w-full h-full transform -rotate-90" viewBox="0 0 96 96">
                                    <defs>
                                        <linearGradient id="circleGradDark" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#e50914" />
                                            <stop offset="100%" stopColor="#ff4b2b" />
                                        </linearGradient>
                                        <linearGradient id="circleGradLight" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#cb0c18" />
                                            <stop offset="100%" stopColor="#ff3838" />
                                        </linearGradient>
                                    </defs>
                                    
                                    {/* High-tech rotating dashed outer ring */}
                                    <circle 
                                        cx="48" 
                                        cy="48" 
                                        r="43" 
                                        fill="transparent" 
                                        strokeWidth="1" 
                                        strokeDasharray="6 4"
                                        className="progress-dash-ring animate-spin-slow"
                                    />

                                    {/* Circle background track */}
                                    <circle 
                                        cx="48" 
                                        cy="48" 
                                        r={radius} 
                                        fill="transparent" 
                                        strokeWidth={strokeWidth} 
                                        className="progress-track"
                                    />
                                    
                                    {/* Dynamic stroke progress */}
                                    <circle 
                                        cx="48" 
                                        cy="48" 
                                        r={radius} 
                                        fill="transparent" 
                                        stroke="url(#circleGradLight)" 
                                        strokeWidth={strokeWidth} 
                                        className="progress-stroke animate-progress-sweep"
                                    />

                                    {/* Perfectly smooth CSS animated comet head dot */}
                                    <g className="animate-comet-rotate">
                                        <circle 
                                            cx="84" 
                                            cy="48" 
                                            r="3.5" 
                                            className="comet-dot-circle"
                                        />
                                    </g>
                                </svg>
                                
                                {/* Inner circle with animated arrow */}
                                <div className="progress-inner-circle w-16 h-16 rounded-full border flex items-center justify-center">
                                    <div className="animate-arrow-slide">
                                        <ArrowRightIcon className="progress-arrow w-5 h-5" />
                                    </div>
                                </div>
                            </div>

                            {/* Text Block */}
                            <span className="redirect-label text-[10px] font-black tracking-[0.3em] uppercase select-none mb-1.5">
                                Redirecting To
                            </span>
                            <h3 
                                className="redirect-title text-xl sm:text-2xl font-black truncate max-w-full select-none leading-snug"
                                style={{
                                    textShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                }}
                            >
                                {redirectSite}
                            </h3>

                            {/* Subtle Cancel Instruction Hint */}
                            <span className="text-[9px] text-[var(--secondary-text)]/40 tracking-widest font-bold uppercase mt-8 select-none">
                                Click outside to cancel
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default React.memo(RedirectOverlay);